/**
 * RadioGroup - 라디오 버튼 그룹 컴포넌트
 *
 * @param options - 선택 항목 배열 (value, label, description, disabled)
 * @param orientation - 'horizontal' | 'vertical' 배치 방향
 */
'use client';

import React from 'react';
import styles from './Radio.module.css';

export interface RadioOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface RadioGroupProps {
  options: RadioOption[];
  value: string;
  onChange: (value: string) => void;
  name: string;
  label?: string;
  orientation?: 'horizontal' | 'vertical';
  size?: 'sm' | 'md';
  disabled?: boolean;
  error?: string;
  className?: string;
}

export default function RadioGroup({
  options,
  value,
  onChange,
  name,
  label,
  orientation = 'vertical',
  size = 'md',
  disabled = false,
  error,
  className = '',
}: RadioGroupProps) {
  return (
    <fieldset className={`${styles.fieldset} ${className}`} aria-invalid={Boolean(error)}>
      {label && <legend className={styles.legend}>{label}</legend>}
      <div className={`${styles.group} ${styles[orientation]}`} role="radiogroup">
        {options.map((option) => {
          const isDisabled = disabled || option.disabled;
          const isSelected = value === option.value;

          return (
            <label
              key={option.value}
              className={`${styles.option} ${isDisabled ? styles.disabled : ''} ${styles[size]}`}
            >
              <span className={styles.control}>
                <input
                  type="radio"
                  className={styles.input}
                  name={name}
                  value={option.value}
                  checked={isSelected}
                  onChange={() => onChange(option.value)}
                  disabled={isDisabled}
                />
                <span className={`${styles.circle} ${isSelected ? styles.selected : ''}`} />
              </span>
              <span className={styles.labelGroup}>
                <span className={styles.label}>{option.label}</span>
                {option.description && (
                  <span className={styles.description}>{option.description}</span>
                )}
              </span>
            </label>
          );
        })}
      </div>
      {error && (
        <p className={styles.errorText} role="alert">
          {error}
        </p>
      )}
    </fieldset>
  );
}
